// ── Calendar ("/calendar") — scoped, inclusive date-range schedule ─────────
// The registered screen stays handwritten, while this page uses the explicit
// calendar read rather than broad sessions.list data. The backend owns org and
// role scope, inclusive date predicates, and malformed/inverted-range errors.
// The range comes from the URL (?from=YYYY-MM-DD&to=YYYY-MM-DD), else the
// current month; Previous / This / Next month links set it (QA F20).

import { Badge, Card, IconCalendar, PageFrame } from "@vjal/ui";
import { Link, useSearchParams } from "react-router-dom";

import { HonestState, useHonestQuery } from "../../components/honest-state";
import { getCoachingServices } from "../../services/coaching-service-provider";
import { coachingQueryKeys } from "../../hooks/coaching-query-keys";
import type { CoachingSessionDto, ListResponse } from "../../services/coaching-dtos";
import type { ProductScreenContext } from "../product-route-contracts";
import {
  bucketSessionsByDay,
  calendarRangeFromSearch,
  calendarRangeHref,
  describeDateRange,
  sessionTimeLabel,
  shiftedMonthDateRange
} from "./calendar-helpers";
import {
  canAccessSessionsModule,
  inviteStatusLabel,
  inviteStatusTone,
  sessionDisplayTitle,
  sessionStatusLabel,
  sessionStatusTone
} from "./sessions-helpers";

const services = getCoachingServices();


export function CalendarPage({ role }: ProductScreenContext) {
  const moduleDenied = !canAccessSessionsModule(role);
  const [searchParams] = useSearchParams();
  const range = calendarRangeFromSearch(searchParams);
  const previousMonth = shiftedMonthDateRange(range, -1);
  const nextMonth = shiftedMonthDateRange(range, 1);
  const query = useHonestQuery<ListResponse<CoachingSessionDto>>(
    coachingQueryKeys.list("sessions.calendar", range),
    () => services.sessions.calendar(range),
    { enabled: !moduleDenied }
  );

  if (moduleDenied) {
    return (
      <section aria-label="Calendar" className="col-span-12" data-testid="calendar-page">
        <div className="rounded-xl border border-line bg-white">
          <HonestState
            posture="permission-denied"
            icon={<IconCalendar size={22} />}
            title="You don't have access to the calendar"
            message="The calendar is for coaching staff. Your own sessions are available on your dashboard."
            />
        </div>
      </section>
    );
  }

  const sessions = query.data?.items ?? [];
  const buckets = bucketSessionsByDay(sessions);

  return (
    <PageFrame
      title="Calendar"
      subtitle="Your scoped sessions grouped by day. On a narrow screen the days stack as a list."
      actions={
        <Link to="/sessions" className="text-link no-underline hover:underline">
          List view
        </Link>
      }
      className="col-span-12"
    >
      <section aria-label="Calendar" className="flex flex-col gap-4" data-testid="calendar-page">
        <nav aria-label="Calendar range" className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm" data-testid="calendar-range">
          <span className="font-semibold text-strong" data-testid="calendar-range-label">
            {describeDateRange(range)}
          </span>
          {previousMonth ? (
            <Link to={calendarRangeHref(searchParams, previousMonth)} className="text-link no-underline hover:underline">
              Previous month
            </Link>
          ) : null}
          <Link to={calendarRangeHref(searchParams, null)} className="text-link no-underline hover:underline">
            This month
          </Link>
          {nextMonth ? (
            <Link to={calendarRangeHref(searchParams, nextMonth)} className="text-link no-underline hover:underline">
              Next month
            </Link>
          ) : null}
        </nav>
        <div className="rounded-xl border border-line bg-white p-5 space-y-4">
          {query.posture !== null ? (
            <HonestState
              posture={query.posture}
              gate={query.gate}
              reference={query.reference}
              onRetry={query.retry}
              icon={<IconCalendar size={22} />}
              loadingVariant="rows"
              title={query.posture === "empty" ? "No sessions scheduled" : undefined}
              message={
                query.posture === "empty"
                  ? "Sessions scheduled in your scope will appear on the calendar."
                  : undefined
              }
              />
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="calendar-grid">
              {buckets.map((bucket) => (
                <Card
                  key={bucket.dateKey}
                  className="shadow-card transition-shadow duration-150 ease-out-soft hover:shadow-card-hover"
                  data-testid={`calendar-day-${bucket.dateKey}`}
                  data-exact-set-name="calendar-day"
                >
                  <div className="mb-3 text-sm font-semibold text-strong">{bucket.label}</div>
                  <ul className="m-0 list-none space-y-3 p-0">
                    {bucket.sessions.map((session) => (
                      <li key={session.id} className="border-t border-line pt-3 first:border-0 first:pt-0">
                        <Link
                          to={`/sessions/${encodeURIComponent(session.id)}`}
                          className="font-medium text-link no-underline hover:underline"
                          data-exact-set-name="Open session"
                        >
                          {sessionDisplayTitle(session.title, session.id)}
                        </Link>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-ink-muted">
                          <span>{sessionTimeLabel(session)}</span>
                          {session.cohort_name?.trim() ? <span>· {session.cohort_name}</span> : null}
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2">
                          <Badge variant={sessionStatusTone(session.status)}>{sessionStatusLabel(session.status)}</Badge>
                          <Badge variant={inviteStatusTone(session.invite_status)}>{inviteStatusLabel(session.invite_status)}</Badge>
                        </div>
                      </li>
                    ))}
                  </ul>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
    </PageFrame>
  );
}
